import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Pin, Lock, Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import client, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loading, Card, Pill } from "../components/common";
import { AdSlot } from "../components/AdSlot";

export function BoardPage() {
  const { board } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const load = () => client.get(`/forum/${board}`).then((r) => setData(r.data));
  useEffect(() => { setData(null); load(); }, [board]);
  if (data === null) return <Loading />;
  if (!data) return <div className="py-20 text-center">Board not found.</div>;

  const create = async () => {
    try {
      await client.post("/forum/thread", { board_slug: board, ...form });
      setShow(false); setForm({ title: "", body: "" }); load(); toast.success("Thread posted");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="board-page">
      <Link to="/forum" className="inline-flex items-center gap-1 text-sm text-ink/50 hover:text-green mb-3"><ArrowLeft size={14} /> All boards</Link>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-head font-black text-3xl text-green">{data.board.name}</h1>
        {user && <button onClick={() => setShow(!show)} data-testid="new-thread-btn" className="flex items-center gap-1 px-4 py-2 bg-gold text-ink font-bold rounded-sm hover:bg-green hover:text-cream transition-colors"><Plus size={16} /> New thread</button>}
      </div>
      {show && (
        <Card className="p-4 mb-4 space-y-2">
          <input data-testid="thread-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Thread title" className="w-full border border-pslborder rounded-sm p-2.5 text-sm focus:outline-none focus:border-gold" />
          <textarea data-testid="thread-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Say something…" rows={3} className="w-full border border-pslborder rounded-sm p-2.5 text-sm focus:outline-none focus:border-gold" />
          <div className="flex justify-end"><button onClick={create} className="px-4 py-2 bg-green text-cream font-bold rounded-sm">Post thread</button></div>
        </Card>
      )}
      <Card className="divide-y divide-pslborder">
        {data.threads.map((t) => (
          <Link to={`/forum/${board}/${t.id}`} key={t.id} className="flex items-center gap-3 p-4 hover:bg-cream" data-testid={`thread-${t.id}`}>
            {t.pinned && <Pin size={14} className="text-gold" />}
            {t.locked && <Lock size={14} className="text-ink/40" />}
            <div className="flex-1">
              <div className="font-semibold text-green">{t.title}</div>
              <div className="text-xs text-ink/50">{t.author} · {t.reply_count} repl{t.reply_count === 1 ? "y" : "ies"}</div>
            </div>
          </Link>
        ))}
      </Card>
    </div>
  );
}

export function ThreadPage() {
  const { thread } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const load = () => client.get(`/forum/thread/${thread}`).then((r) => setData(r.data));
  useEffect(() => { setData(null); load(); }, [thread]);
  if (data === null) return <Loading />;
  if (!data) return <div className="py-20 text-center">Thread not found.</div>;
  const { thread: t, replies } = data;
  const canMod = user && ["super_master_admin", "super_admin", "psl_staffer", "media"].includes(user.role);

  const reply = async () => {
    if (!body.trim()) return;
    try { await client.post(`/forum/thread/${thread}/reply`, { body }); setBody(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const mod = async (action) => { await client.post(`/forum/thread/${thread}/moderate?action=${action}`); load(); };
  const report = async (target_type, target_id) => {
    const reason = window.prompt("Report — reason (abuse, spam, etc.):", "");
    if (reason === null) return;
    try { await client.post(`/report`, { target_type, target_id, reason }); toast.success("Reported to moderators"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="grid lg:grid-cols-4 gap-6" data-testid="thread-page">
      <div className="lg:col-span-3">
        <Card className="p-5 mb-4">
          <div className="flex items-center gap-2 mb-2">{t.pinned && <Pill tone="gold">Pinned</Pill>}{t.locked && <Pill tone="muted">Locked</Pill>}</div>
          <h1 className="font-head font-black text-2xl text-green">{t.title}</h1>
          <p className="text-xs text-ink/50 mt-1">{t.author} · {new Date(t.created_at).toLocaleString("en-GB")}</p>
          <p className="mt-3 text-ink/90">{t.body}</p>
          {user && <button onClick={() => report("forum_thread", t.id)} className="mt-3 text-xs text-ink/40 hover:text-live block" data-testid="report-thread">Report thread</button>}
          {canMod && (
            <div className="flex gap-3 mt-4 pt-3 border-t border-pslborder">
              <button onClick={() => mod(t.pinned ? "unpin" : "pin")} data-testid="mod-pin" className="text-xs font-bold text-green hover:text-gold">{t.pinned ? "Unpin" : "Pin"}</button>
              <button onClick={() => mod(t.locked ? "unlock" : "lock")} data-testid="mod-lock" className="text-xs font-bold text-green hover:text-gold">{t.locked ? "Unlock" : "Lock"}</button>
            </div>
          )}
        </Card>
        <div className="space-y-2">
          {replies.map((r) => (
            <Card key={r.id} className="p-4"><div className="flex justify-between text-xs text-ink/50 mb-1"><span className="font-bold text-green">{r.author}</span><span>{new Date(r.created_at).toLocaleString("en-GB")}</span></div><p className="text-ink/90 text-sm">{r.body}</p>{user && <button onClick={() => report("forum_reply", r.id)} className="mt-2 text-xs text-ink/40 hover:text-live" data-testid={`report-reply-${r.id}`}>Report</button>}</Card>
          ))}
        </div>
        {user && !t.locked && (
          <Card className="p-4 mt-4">
            <textarea data-testid="reply-input" value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Reply…" className="w-full border border-pslborder rounded-sm p-2.5 text-sm focus:outline-none focus:border-gold" />
            <div className="flex justify-end mt-2"><button onClick={reply} data-testid="reply-btn" className="px-4 py-2 bg-green text-cream font-bold rounded-sm">Reply</button></div>
          </Card>
        )}
        {!user && <p className="text-sm text-ink/50 mt-4">Sign in to reply.</p>}
      </div>
      <aside><AdSlot id="community.forum_thread_sidebar" height={300} /></aside>
    </div>
  );
}
